/**
 * Minimal toast system. No dependency — just React context + a render portal
 * fixed at the bottom-right. Self-dismisses after a configurable duration.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error(err.message);
 *   toast.info('Heads up');
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type Variant = 'success' | 'error' | 'info';

interface ToastItem {
  id: string;
  variant: Variant;
  message: string;
  /** Optional secondary line (e.g. error details). */
  detail?: string;
}

interface ToastApi {
  success: (message: string, detail?: string) => void;
  error: (message: string, detail?: string) => void;
  info: (message: string, detail?: string) => void;
  dismiss: (id: string) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const v = useContext(ToastCtx);
  if (!v) throw new Error('useToast must be used inside <ToastProvider>');
  return v;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setItems((xs) => xs.filter((x) => x.id !== id));
    const t = timers.current.get(id);
    if (t) { clearTimeout(t); timers.current.delete(id); }
  }, []);

  const push = useCallback(
    (variant: Variant, message: string, detail?: string, ttl = 4500) => {
      const id = crypto.randomUUID();
      setItems((xs) => [...xs, { id, variant, message, detail }]);
      const timeout = setTimeout(() => dismiss(id), ttl);
      timers.current.set(id, timeout);
    },
    [dismiss],
  );

  useEffect(() => {
    // Snapshot the timers Map at effect-setup time; cleanup reads from the snapshot
    // instead of mutating `.current` on unmount (avoids the stale-ref lint).
    const snapshot = timers.current;
    return () => {
      snapshot.forEach((t) => clearTimeout(t));
      snapshot.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, d) => push('success', m, d),
      error:   (m, d) => push('error',   m, d, 7000),
      info:    (m, d) => push('info',    m, d),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-[min(92vw,380px)] pointer-events-none"
      >
        {items.map((t) => (
          <ToastView key={t.id} item={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

const VARIANT_STYLE: Record<Variant, string> = {
  success: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  error:   'bg-red-50 text-red-900 border-red-200',
  info:    'bg-blue-50 text-blue-900 border-blue-200',
};

const VARIANT_ICON: Record<Variant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error:   AlertTriangle,
  info:    Info,
};

function ToastView({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const Icon = VARIANT_ICON[item.variant];
  return (
    <div
      role={item.variant === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto rounded-md border shadow-sm px-3 py-2.5 flex items-start gap-2 animate-in fade-in slide-in-from-bottom-2',
        VARIANT_STYLE[item.variant],
      )}
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium break-words">{item.message}</div>
        {item.detail && (
          <div className="text-xs opacity-80 mt-0.5 break-words">{item.detail}</div>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded p-0.5 hover:bg-black/5"
        aria-label="Dismiss notification"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
